'use client';

import { clsx } from 'clsx';
import { FC, ReactNode, useRef } from 'react';

import { IconCaretLeft } from 'src/general/Icons/IconCaretLeft';
import { IconCaretRight } from 'src/general/Icons/IconCaretRight';

import { ButtonIcon } from '../Button/ButtonIcon';
import { ScrollBox } from '../ScrollBox/ScrollBox';
import styles from './Carousel.module.scss';

type Props = {
  className?: string;
  tiles: { id: string; tile: ReactNode }[];
  controls?: {
    prev: (onClick: () => void) => ReactNode;
    next: (onClick: () => void) => ReactNode;
  };
  carouselName?: string;
  variant?: 'recently-viewed' | 'default';
};

export const Carousel: FC<Props> = ({ className, tiles, controls, carouselName, variant = 'default' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const handleNextClick = () => {
    if (rootRef.current) {
      const scrollWidth = getScrollByLength(rootRef.current);
      const carouselTrack = rootRef.current.querySelector('.carouselTrack');

      if (scrollWidth !== null && carouselTrack !== null) {
        carouselTrack.scrollTo({
          left: Math.min(carouselTrack.scrollWidth, carouselTrack.scrollLeft + scrollWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  const handlePrevClick = () => {
    if (rootRef.current) {
      const scrollWidth = getScrollByLength(rootRef.current);
      const carouselTrack = rootRef.current.querySelector('.carouselTrack');

      if (scrollWidth !== null && carouselTrack !== null) {
        carouselTrack.scrollTo({
          left: Math.max(0, carouselTrack.scrollLeft - scrollWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  const getScrollBoxTestId = (carouselName?: string) => {
    return carouselName ? `${carouselName}-scrollbox` : 'carousel-scrollbox';
  };

  return (
    <div className={clsx(styles.root, 'carousel', className)} ref={rootRef} data-testid={carouselName}>
      <ScrollBox
        className={clsx(styles.track, 'carouselTrack', {
          'ml-0! pl-0! md:-ml-7! md:pl-7!': variant === 'recently-viewed',
        })}
        data-testid={getScrollBoxTestId(carouselName)}
      >
        {tiles.map(({ id, tile }, index) => (
          <div className={clsx(styles.tile, 'carouselTile')} key={id} data-index={index} data-testid="carouselTile">
            {tile}
          </div>
        ))}
      </ScrollBox>
      <div
        className={clsx(styles.controls, 'carouselControls', {
          'md:left-0!': variant === 'recently-viewed',
        })}
      >
        {controls ? (
          <>
            {/* eslint-disable-next-line react-hooks/refs */}
            {controls.prev(handlePrevClick)}
            {/* eslint-disable-next-line react-hooks/refs */}
            {controls.next(handleNextClick)}
          </>
        ) : (
          <>
            <ButtonIcon
              title="Previous slide"
              Icon={IconCaretLeft}
              variant="secondary"
              size="small"
              styling="square"
              className={clsx(styles.prev, 'carouselPrev')}
              onClick={handlePrevClick}
              data-testid={carouselName ? `${carouselName}-navigation-prev` : 'carousel-prev'}
            />
            <ButtonIcon
              title="Next slide"
              Icon={IconCaretRight}
              variant="secondary"
              size="small"
              styling="square"
              className={clsx(styles.next, 'carouselNext')}
              onClick={handleNextClick}
              data-testid={carouselName ? `${carouselName}-navigation-next` : 'carousel-next'}
            />
          </>
        )}
      </div>
    </div>
  );
};

const getScrollByLength = (root: HTMLDivElement): number | null => {
  const rootWidth = root.clientWidth;
  // Get the list of tiles in the carousel
  const tiles = root.querySelectorAll('.carouselTile');
  if (tiles.length) {
    const tileWidth = tiles[0].clientWidth;
    // Scroll by at least a tile width or 60% of the track
    const scrollWidth = Math.max(tileWidth, Math.floor(rootWidth / tileWidth) * 0.6 * tileWidth);

    return scrollWidth;
  }

  return null;
};
